import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { OrgAdminSidebarComponent } from '../sidebar/org-admin-sidebar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { AssetRequestComponent } from './asset-request/asset-request.component';
import { PublishAssetsComponent } from './publish-assets/publish-assets.component';
import { CreatedPoliciesComponent } from './created-policies/created-policies.component';
import { WorkspaceContextService } from '../../services/workspace-context.service';

@Component({
  selector: 'app-access-requests-home',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    OrgAdminSidebarComponent,
    PageHeaderComponent,
    AssetRequestComponent,
    PublishAssetsComponent,
    CreatedPoliciesComponent,
  ],
  templateUrl: './access-requests-home.component.html',
  styleUrl: './access-requests-home.component.scss',
})
export class AccessRequestsHomeComponent implements OnInit {
  @ViewChild(AssetRequestComponent) assetRequestComponent?: AssetRequestComponent;
  @ViewChild(PublishAssetsComponent) publishAssetsComponent?: PublishAssetsComponent;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private workspaceContext: WorkspaceContextService,
  ) {}

  activeTab: 'assetRequest' | 'publishAssets' | 'policies' = 'assetRequest';

  get isPoliciesTab(): boolean {
    return this.activeTab === 'policies';
  }

  get showPublishAssetsTab(): boolean {
    return this.workspaceContext.canShowPublishAssetsTab();
  }

  get showAssetRequestTab(): boolean {
    return this.workspaceContext.canShowAssetRequestTab();
  }

  get showInnerTabs(): boolean {
    return this.showAssetRequestTab || this.showPublishAssetsTab;
  }

  get pageTitle(): string {
    return this.isPoliciesTab ? 'Access Policy' : 'My Asset Requests';
  }

  get pageDescription(): string {
    if (this.isPoliciesTab) {
      return 'Review and manage organisation access policies.';
    }
    if (this.activeTab === 'publishAssets') {
      return 'Review and manage assets submitted for publishing.';
    }
    return 'Review and manage asset access requests.';
  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params) => {
      const tabFromUrl = params.get('tab');
      const resolved = this.resolveTab(tabFromUrl);
      if (tabFromUrl && resolved === this.normalizeUrlTab(tabFromUrl)) {
        this.activeTab = resolved;
        return;
      }

      this.activeTab = resolved;
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { tab: resolved },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    });
  }

  setActiveTab(tab: 'assetRequest' | 'publishAssets'): void {
    if (tab === 'publishAssets' && !this.showPublishAssetsTab) {
      return;
    }
    if (tab === 'assetRequest' && !this.showAssetRequestTab) {
      return;
    }
    if (this.activeTab === tab) {
      return;
    }
    this.assetRequestComponent?.collapseExpandedRows();
    this.publishAssetsComponent?.collapseExpandedRows();
    this.activeTab = tab;

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private normalizeUrlTab(
    tabFromUrl: string,
  ): 'assetRequest' | 'publishAssets' | 'policies' {
    if (tabFromUrl === 'createdPolicies' || tabFromUrl === 'policies') {
      return 'policies';
    }
    if (tabFromUrl === 'publishAssets') return 'publishAssets';
    return 'assetRequest';
  }

  private resolveTab(
    tabFromUrl: string | null,
  ): 'assetRequest' | 'publishAssets' | 'policies' {
    if (!tabFromUrl) {
      return this.defaultRequestTab();
    }
    const tab = this.normalizeUrlTab(tabFromUrl);
    if (tab === 'policies') return 'policies';
    if (tab === 'publishAssets' && this.showPublishAssetsTab) {
      return 'publishAssets';
    }
    if (tab === 'assetRequest' && this.showAssetRequestTab) {
      return 'assetRequest';
    }
    return this.defaultRequestTab();
  }

  private defaultRequestTab(): 'assetRequest' | 'publishAssets' {
    if (this.showAssetRequestTab) return 'assetRequest';
    if (this.showPublishAssetsTab) return 'publishAssets';
    return 'assetRequest';
  }
}
